import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { hojeISO } from "utils/dataCalendario";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v) || 0
  );

const paraNumero = (v) =>
  Number(
    String(v ?? "")
      .replace(/\./g, "")
      .replace(",", ".")
  ) || 0;

const paraCampo = (centavos) => (centavos / 100).toFixed(2).replace(".", ",");

const somarDias = (dataISO, dias) => {
  const d = new Date(`${dataISO}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
};

// Mesmo dia nos meses seguintes (31/01 + 1 mês = 28/02 ou 29/02).
const somarMeses = (dataISO, meses) => {
  const [a, m, d] = dataISO.split("-").map(Number);
  const ultimoDia = new Date(Date.UTC(a, m - 1 + meses + 1, 0)).getUTCDate();
  return new Date(Date.UTC(a, m - 1 + meses, Math.min(d, ultimoDia)))
    .toISOString()
    .slice(0, 10);
};

// Divide o total (centavos) em n parcelas; a diferença de centavos vai na última.
const dividir = (total, n) => {
  const base = Math.floor(total / n);
  return Array.from({ length: n }, (_, i) =>
    i === n - 1 ? total - base * (n - 1) : base
  );
};

/**
 * Parcela uma conta a pagar/receber em N contas. Só o valor em aberto é
 * parcelado (a API desconta baixas parciais). `service` é o PayablesService
 * ou ReceivablesService (métodos parcelamento e parcelar).
 */
export default function ParcelarConta({
  open,
  contaId,
  service,
  vencimento,
  onClose,
  onParcelada,
}) {
  const [aberto, setAberto] = useState(null);
  const [quantidade, setQuantidade] = useState(2);
  const [primeiro, setPrimeiro] = useState("");
  const [intervalo, setIntervalo] = useState("mensal"); // "mensal" ou nº de dias
  const [parcelas, setParcelas] = useState([]);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open || !contaId) return;
    setErro("");
    setPrimeiro(
      (vencimento && String(vencimento).slice(0, 10)) ||
        hojeISO()
    );
    service
      .parcelamento(contaId)
      .then((r) => setAberto(Math.round((r.aberto || 0) * 100)))
      .catch((e) =>
        setErro(e?.error || e?.message || "Erro ao carregar a conta.")
      );
  }, [open, contaId]);

  // Recalcula as parcelas quando muda quantidade, 1º vencimento ou intervalo.
  useEffect(() => {
    const n = Number(quantidade);
    if (!aberto || !primeiro || !(n >= 2)) return setParcelas([]);
    setParcelas(
      dividir(aberto, n).map((valor, i) => ({
        valor: paraCampo(valor),
        dataVencimento:
          intervalo === "mensal"
            ? somarMeses(primeiro, i)
            : somarDias(primeiro, i * (Number(intervalo) || 0)),
      }))
    );
  }, [aberto, quantidade, primeiro, intervalo]);

  const soma = useMemo(
    () =>
      parcelas.reduce(
        (acc, p) => acc + Math.round(paraNumero(p.valor) * 100),
        0
      ),
    [parcelas]
  );
  const diferenca = (aberto || 0) - soma;

  const alterar = (i, campo, valor) =>
    setParcelas((atual) =>
      atual.map((p, j) => (j === i ? { ...p, [campo]: valor } : p))
    );

  const confirmar = async () => {
    setSalvando(true);
    setErro("");
    try {
      await service.parcelar(contaId, {
        parcelas: parcelas.map((p) => ({
          valor: paraNumero(p.valor),
          dataVencimento: `${p.dataVencimento}T12:00:00`,
        })),
      });
      onParcelada?.();
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao parcelar.");
    }
    setSalvando(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 10000 }}
    >
      <DialogTitle>Parcelar conta</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          Valor em aberto: <b>{aberto === null ? "..." : brl(aberto / 100)}</b>.
          A conta vira {quantidade || "N"} contas, cada uma com vencimento e
          baixa próprios. Se ela já teve baixa parcial, fica com o valor pago e
          as parcelas cobrem o resto.
        </Typography>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={4}>
            <TextField
              label="Parcelas"
              type="number"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              inputProps={{ min: 2, max: 60 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="1º vencimento"
              type="date"
              value={primeiro}
              onChange={(e) => setPrimeiro(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Intervalo"
              select
              SelectProps={{ native: true }}
              value={intervalo}
              onChange={(e) => setIntervalo(e.target.value)}
              fullWidth
            >
              <option value="mensal">Mensal (mesmo dia)</option>
              <option value="7">A cada 7 dias</option>
              <option value="15">A cada 15 dias</option>
              <option value="30">A cada 30 dias</option>
            </TextField>
          </Grid>
        </Grid>

        {parcelas.length > 0 && (
          <Table size="small">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>Parcela</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Valor (R$)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parcelas.map((p, i) => (
                <TableRow key={i}>
                  <TableCell>
                    {i + 1}/{parcelas.length}
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={p.dataVencimento}
                      onChange={(e) =>
                        alterar(i, "dataVencimento", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={p.valor}
                      onChange={(e) =>
                        alterar(
                          i,
                          "valor",
                          e.target.value.replace(/[^\d,]/g, "")
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {parcelas.length > 0 && diferenca !== 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            A soma das parcelas ({brl(soma / 100)}) está{" "}
            {diferenca > 0 ? "abaixo" : "acima"} do valor em aberto em{" "}
            {brl(Math.abs(diferenca) / 100)}.
          </Alert>
        )}
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          sx={{ color: "white !important" }}
          disabled={salvando || parcelas.length < 2 || diferenca !== 0}
          onClick={confirmar}
        >
          {salvando ? "Parcelando..." : "Parcelar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
